import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SENSITIVE_AUDIT_TYPES } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useRpc } from '../data/people.ts'
import { formatLocal } from '../lib/time.ts'
import { Banner, errorText, Pill } from './components.tsx'
import { Picto } from './pictos.tsx'

interface Change { column: string; old?: unknown; new?: unknown; hidden?: boolean }
interface HistoryEvent {
  id: string; at: string; action: string; command_id: string | null; actor: string | null; device: string | null; kiosk: boolean; status: string | null; exceptions: string[]; changes: Change[] | null
}
interface HistoryAnswer { events: HistoryEvent[]; truncated: boolean }

const show = (v: unknown, nothing: string): string => {
  if (v === null || v === undefined || v === '') return nothing
  if (typeof v === 'string') return v.length > 80 ? `${v.slice(0, 79)}…` : v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  const j = JSON.stringify(v)
  return j.length > 80 ? `${j.slice(0, 79)}…` : j
}

/** The "Historique" of one record (GK-045): who did what, from which device, when and what changed. Read online, only by those who may see history (the head greenkeeper, the manager, the
 *  administrator, and the mechanic for the fleet and the catalogue); closed until asked for, so a screen costs nothing when nobody looks. A sensitive value is never in it: the server sends
 *  only the names of the columns that changed. */
export function History({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { t } = useTranslation()
  const { org, can } = useClub()
  const [open, setOpen] = useState(false)
  const [limit, setLimit] = useState(50)
  // release b review (findings 19, 27): the trail of a machine's financials, a cost or an incident needs that record's own reading permission (the server's rule,
  // api.entity_history; its second factor too, which the server asks)
  const gate = SENSITIVE_AUDIT_TYPES[entityType]
  const allowed = (can('audit.read') || (/^(fleet|catalog)\./.test(entityType) && can('asset.manage'))) && (!gate || can(gate))
  const q = useRpc<HistoryAnswer>('entity_history', { p_org: org.org_id, p_entity_type: entityType, p_entity_id: entityId, p_limit: limit }, open && allowed)
  if (!allowed) return null
  // M23 item 60: closed, it is one 56-px row with a chevron (it looked like an empty card); open, a card, with the count once the events are loaded
  return (
    <details className={open ? 'card' : 'rounded-2xl border border-transparent px-4'} data-testid="history" onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-3 text-lg font-bold [&::-webkit-details-marker]:hidden" data-testid="history-toggle">
        <span className="flex items-center gap-2"><Picto name="history" className="size-6 text-brand dark:text-fairway" />{t('history.title')}</span>
        <span className="flex items-center gap-2">
          {open && q.data && <span className="meta" data-testid="history-count">{t('history.events', { count: q.data.events.length })}{q.data.truncated ? '+' : ''}</span>}
          <Picto name="chevron" className={`size-5 shrink-0 transition-transform ${open ? '-rotate-90' : 'rotate-90'}`} strokeWidth={2.2} />
        </span>
      </summary>
      {open && (
        <div className="mt-2 flex flex-col gap-3">
          {q.online === false && <Banner tone="info">{t('history.offline')}</Banner>}
          {q.error && <Banner tone="error">{errorText(t, q.error)}</Banner>}
          {!q.data && q.online !== false && !q.error && <p>{t('history.loading')}</p>}
          {q.data && q.data.events.length === 0 && <p className="opacity-70" data-testid="history-empty">{t('history.empty')}</p>}
          {q.data && q.data.events.length > 0 && (
            <ul className="flex flex-col gap-3" data-testid="history-list">
              {q.data.events.map((e) => <Row key={e.id} e={e} />)}
            </ul>
          )}
          {q.data?.truncated && <button className="btn" onClick={() => setLimit((n) => Math.min(300, n + 100))} data-testid="history-more">{t('history.showMore')}</button>}
        </div>
      )}
    </details>
  )
}

function Row({ e }: { e: HistoryEvent }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const action = t(`history.action.${e.action.replace(/\./g, '_')}`, { defaultValue: e.action })
  return (
    <li className="flex flex-col gap-1 border-l-4 border-slate-300 pl-3 dark:border-slate-600" data-testid="history-event" data-action={e.action}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{action}</span>
        {e.kiosk && <Pill tone="gray">{t('history.kiosk')}</Pill>}
        {e.status && e.status !== 'applied' && e.status !== 'applied_with_exception' && <Pill tone="red">{t(`history.status_${e.status}`, { defaultValue: e.status })}</Pill>}
      </div>
      <p className="text-sm opacity-80">
        {formatLocal(e.at, site.timezone, i18n.language.startsWith('en') ? 'yyyy-MM-dd HH:mm' : 'dd/MM/yyyy HH:mm')} · {t('history.by', { who: e.actor ?? t('history.system') })}
        {e.device ? ` · ${t('history.device', { label: e.device })}` : ''}
      </p>
      {e.exceptions.length > 0 && <ul className="text-sm text-amber-800 dark:text-amber-300">{e.exceptions.map((c) => <li key={c}>{errorText(t, c)}</li>)}</ul>}
      {e.changes && e.changes.length > 0 && (
        <ul className="text-sm" data-testid="history-changes">
          {e.changes.map((c) => {
            const column = t(`history.column.${c.column}`, { defaultValue: c.column })
            return (
              <li key={c.column} data-column={c.column}>
                {c.hidden ? `${column} (${t('history.hidden')})`
                  : c.old === null || c.old === undefined ? t('history.set', { column, new: show(c.new, t('history.nothing')) })
                  : t('history.changed', { column, old: show(c.old, t('history.nothing')), new: show(c.new, t('history.nothing')) })}
              </li>
            )
          })}
        </ul>
      )}
    </li>
  )
}
